// Backend errors that carry a stable, machine-readable code rather than matchable English.
// marketplace-api appends "(code=...)" to its message and mhub-customer-api forwards it, because an
// AppSync Go resolver reaches Apollo only through errors[0].message (CPI-1303).
const ERROR_CODE_MAP = {
  DUPLICATE_MOBILE: {
    key: 'error.srb_duplicate_mobile',
    fallback: 'An account with this mobile number already exists. Please search for the existing customer, or use a different mobile number.',
  },
  DUPLICATE_EMAIL: {
    key: 'error.srb_duplicate_email',
    fallback: 'An account with this email address already exists. Please search for the existing customer, or use a different email.',
  },
  DUPLICATE_ACCOUNT: {
    key: 'error.srb_duplicate_account',
    fallback: 'An account with these details already exists. Please search for the existing customer.',
  },
};

const CODE_PATTERN = /\(code=([A-Z_]+)\)/;

const DEFAULT_FALLBACK = { key: 'error.generic', fallback: 'Something went wrong. Please try again later.' };

// Shown when SRB account creation fails for a reason we cannot name. More useful than the generic
// here, because it tells the user the account was not created and who to ask.
export const SRB_ACCOUNT_ERROR_FALLBACK = {
  key: 'error.srb_account_failed',
  fallback: 'Could not create the account. Please contact support.',
};

const ERROR_KEY_MAP = [
  { pattern: /access denied/i, key: 'component.label.access_denied', fallback: 'Access Denied' },
  { pattern: /unauthorized/i, key: 'component.label.access_denied', fallback: 'Access Denied' },
  { pattern: /forbidden/i, key: 'component.label.access_denied', fallback: 'Access Denied' },
  { pattern: /failed to create booking token/i, key: 'error.booking_token_failed', fallback: 'Failed to create booking token.' },
  { pattern: /failed to (retrieve|get) (opportunity|booking opportunity)/i, key: 'error.booking_opportunity_failed', fallback: 'Failed to retrieve booking opportunity.' },
  { pattern: /failed to create (related purchaser contact|purchaser contact)/i, key: 'error.create_contact_failed', fallback: 'Failed to create purchaser contact.' },
  { pattern: /failed to create (related purchaser record|purchaser record)/i, key: 'error.create_customer_failed', fallback: 'Failed to create purchaser record.' },
  { pattern: /something went wrong/i, key: 'error.generic', fallback: 'Something went wrong. Please try again later.' },
];

// `fallback` overrides the message shown when nothing matches, for callers where "something went
// wrong" is vaguer than the situation warrants. Unmatched errors NEVER render the backend string:
// that is how raw Cognito exceptions used to reach the toast.
export const parseApiError = (error, t, fallback = DEFAULT_FALLBACK) => {
  const raw = error?.graphQLErrors?.[0]?.message || error?.message || '';

  const jsonMatch = raw.match(/\{[\s\S]*\}/);
  let msg = raw;
  if (jsonMatch) {
    try {
      msg = JSON.parse(jsonMatch[0])?.error_message || raw;
    } catch {
      /* noop */
    }
  }

  if (!msg) {
    return t(fallback.key, fallback.fallback);
  }

  // A code is checked before the English patterns: it is exact, and it survives translation of the
  // message it is attached to.
  const code = msg.match(CODE_PATTERN);
  const byCode = code && ERROR_CODE_MAP[code[1]];
  if (byCode) {
    return t(byCode.key, byCode.fallback);
  }

  const matched = ERROR_KEY_MAP.find(({ pattern }) => pattern.test(msg));
  if (matched) {
    return t(matched.key, matched.fallback);
  }

  return t(fallback.key, fallback.fallback);
};

export default parseApiError;
